import { useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft, DollarSign, CreditCard, HelpCircle, Send, 
  Paperclip, X, Loader2, CheckCircle, AlertCircle
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";

const ContactPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";
  
  // Get initial tab from URL query param
  const initialTab = searchParams.get("tab") || "support";

  // Deep-link support: `/contact?ref=account_status&status=frozen&user=<id>`
  // — pre-selects the Support tab, prefills subject/message, and pins
  // the user_id + reason in the body so support reps see context in one
  // click (used by NoticeBlock and BlockedPage).
  const deepLinkRef = searchParams.get("ref");
  const deepLinkStatus = searchParams.get("status");
  const deepLinkUser = searchParams.get("user");

  const _isAccountStatusDeepLink = deepLinkRef === "account_status" && !!deepLinkStatus;
  const _defaultTab = _isAccountStatusDeepLink ? "support" : initialTab;
  const _defaultSubject = _isAccountStatusDeepLink
    ? `Account under review — status: ${deepLinkStatus.toUpperCase()}`
    : "";
  const _defaultMessage = _isAccountStatusDeepLink
    ? (
        "Hello — I received a notice that my account is currently "
        + `\`${deepLinkStatus}\`. Please let me know what's needed to resolve this.\n\n`
        + "---\n"
        + `ref: account_status\nstatus: ${deepLinkStatus}\n`
        + (deepLinkUser ? `user_id: ${deepLinkUser}\n` : "")
        + (user?.email ? `email: ${user.email}\n` : "")
      )
    : "";

  const [activeTab, setActiveTab] = useState(_defaultTab);
  const [subject, setSubject] = useState(_defaultSubject);
  const [message, setMessage] = useState(_defaultMessage);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  
  const fileInputRef = useRef(null);
  
  const categoryMap = {
    sales: "SALES",
    billing: "BILLING",
    support: "SUPPORT"
  };
  
  const categoryInfo = {
    sales: {
      icon: DollarSign,
      title: "Sales Inquiry",
      description: "Questions about pricing, plans, or enterprise solutions",
      color: isCyber ? "text-green-400" : "text-emerald-400"
    },
    billing: {
      icon: CreditCard,
      title: "Billing Support",
      description: "Payment issues, invoices, or subscription changes",
      color: isCyber ? "text-yellow-400" : "text-amber-400"
    },
    support: {
      icon: HelpCircle,
      title: "Technical Support",
      description: "Help with features, bugs, or account issues",
      color: isCyber ? "text-cyan-400" : "text-divine-gold"
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    // Filter valid files
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      return true;
    });
    
    // Check total count
    const newTotal = attachments.length + validFiles.length;
    if (newTotal > maxFiles) {
      toast.error(`Maximum ${maxFiles} attachments allowed`);
      validFiles.splice(maxFiles - attachments.length);
    }
    
    // Convert to base64
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachments(prev => [...prev, {
          filename: file.name,
          data: e.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || subject.trim().length < 5) {
      toast.error("Subject must be at least 5 characters");
      return;
    }
    
    if (!message.trim() || message.trim().length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await authAxios.post("/helpdesk/tickets", {
        category: categoryMap[activeTab],
        subject: subject.trim(),
        message: message.trim(),
        attachments: attachments
      });
      
      setTicketId(response.data.ticket_id);
      setSubmitted(true);
      toast.success("Your request has been submitted!");
      
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };
  
  const resetForm = () => {
    setSubject("");
    setMessage("");
    setAttachments([]);
    setSubmitted(false);
    setTicketId(null);
  };
  
  const currentCategory = categoryInfo[activeTab];
  const CategoryIcon = currentCategory.icon;
  
  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-divine-black text-white">
        <Toaster richColors position="top-right" />
        
        {/* Header */}
        <header className="border-b border-divine-gold/20 bg-divine-charcoal/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-white hover:text-divine-gold"
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="font-cinzel text-xl text-divine-gold">Contact Us</h1>
            </div>
          </div>
        </header>
        
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-xl mx-auto text-center">
            <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
              isCyber ? "bg-cyan-500/20" : "bg-divine-gold/20"
            }`}>
              <CheckCircle className={`w-10 h-10 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
            </div>
            
            <h2 className="font-cinzel text-2xl text-white mb-4">Request Submitted!</h2>
            <p className="text-white/70 mb-2">
              Your {currentCategory.title.toLowerCase()} request has been received.
            </p>
            <p className="text-white/50 text-sm mb-8">
              Ticket ID: <span className={isCyber ? "text-cyan-400" : "text-divine-gold"}>{ticketId}</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/profile?tab=support")}
                className={isCyber 
                  ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                  : "bg-divine-gold hover:bg-divine-gold/90 text-black"
                }
                data-testid="view-tickets-btn"
              >
                View My Tickets
              </Button>
              <Button
                variant="outline"
                onClick={resetForm}
                className="border-white/30 text-white hover:bg-white/10"
                data-testid="submit-another-btn"
              >
                Submit Another Request
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-divine-black text-white">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <header className="border-b border-divine-gold/20 bg-divine-charcoal/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:text-divine-gold"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-cinzel text-xl text-divine-gold">Contact Us</h1>
          </div>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Category Tabs */}
          <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); resetForm(); }} className="mb-8">
            <TabsList className="grid w-full grid-cols-3 bg-divine-charcoal border border-divine-gold/20">
              <TabsTrigger 
                value="sales"
                className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black"
                data-testid="sales-tab"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Sales
              </TabsTrigger>
              <TabsTrigger 
                value="billing"
                className="data-[state=active]:bg-amber-500 data-[state=active]:text-black"
                data-testid="billing-tab"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Billing
              </TabsTrigger>
              <TabsTrigger 
                value="support"
                className={`data-[state=active]:text-black ${
                  isCyber 
                    ? "data-[state=active]:bg-cyan-500" 
                    : "data-[state=active]:bg-divine-gold"
                }`}
                data-testid="support-tab"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Support
              </TabsTrigger>
            </TabsList>
            
            {/* Form Content - same for all tabs */}
            <TabsContent value={activeTab} className="mt-6">
              <div className="glass rounded-xl p-6">
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className={`p-2 rounded-lg ${
                    isCyber ? "bg-cyan-500/20" : "bg-divine-gold/20"
                  }`}>
                    <CategoryIcon className={`w-5 h-5 ${currentCategory.color}`} />
                  </div>
                  <div>
                    <h2 className="font-cinzel text-lg text-white">{currentCategory.title}</h2>
                    <p className="text-white/60 text-sm">{currentCategory.description}</p>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Subject */}
                  <div>
                    <Label className="text-white/80">Subject *</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Brief description of your inquiry"
                      className="mt-2 !bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40 focus:border-divine-gold"
                      maxLength={200}
                      data-testid="subject-input"
                    />
                    <p className="text-white/40 text-xs mt-1">{subject.length}/200 characters</p>
                  </div>
                  
                  {/* Message */}
                  <div>
                    <Label className="text-white/80">Message *</Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Please provide as much detail as possible..."
                      className="mt-2 !bg-[#1a1a1a] border-white/20 text-white placeholder:text-white/40 min-h-[150px] focus:border-divine-gold"
                      maxLength={5000}
                      data-testid="message-input"
                    />
                    <p className="text-white/40 text-xs mt-1">{message.length}/5000 characters</p>
                  </div>
                  
                  {/* Attachments */}
                  <div>
                    <Label className="text-white/80">Attachments (optional)</Label>
                    <div className="mt-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="file-input"
                      />
                      
                      {/* Attachment Preview */}
                      {attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {attachments.map((att, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2 text-sm"
                            >
                              <span className="text-white/80 truncate max-w-[150px]">{att.filename}</span>
                              <button
                                type="button"
                                onClick={() => removeAttachment(idx)}
                                className="text-white/50 hover:text-red-400"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {attachments.length < 5 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="border-white/20 text-white/70 hover:bg-white/10"
                          data-testid="attach-btn"
                        >
                          <Paperclip className="w-4 h-4 mr-2" />
                          Attach Images ({attachments.length}/5)
                        </Button>
                      )}
                      <p className="text-white/40 text-xs mt-2">
                        Attach screenshots or images (max 5 files, 10MB each)
                      </p>
                    </div>
                  </div>
                  
                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      disabled={submitting || !subject.trim() || !message.trim()}
                      className={isCyber 
                        ? "bg-cyan-500 hover:bg-cyan-600 text-black" 
                        : "bg-divine-gold hover:bg-divine-gold/90 text-black"
                      }
                      data-testid="submit-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Existing Tickets Link */}
          <div className="text-center mt-8">
            <p className="text-white/50 text-sm">
              Already submitted a request?{" "}
              <button
                onClick={() => navigate("/profile?tab=support")}
                className={`underline ${isCyber ? "text-cyan-400" : "text-divine-gold"} hover:opacity-80`}
                data-testid="view-existing-tickets-link"
              >
                View your tickets
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
